<template>
  <div class="pa-4 pb-12" style="max-width: 680px; margin: 0 auto;">
    <div class="mb-8">
      <h1 class="text-h5 font-weight-black mb-1">Changelog</h1>
      <p class="text-body-2 text-grey">What's new in Gamedev.id</p>
    </div>

    <div class="d-flex flex-column" style="gap: 40px;">
      <div v-for="release in releases" :key="release.version">
        <!-- Version header -->
        <div class="d-flex align-center ga-3 mb-4">
          <v-chip color="primary" variant="flat" size="small" class="font-weight-black rounded-pill">
            v{{ release.version }}
          </v-chip>
          <span class="text-caption text-grey">{{ release.date }}</span>
          <v-chip v-if="release.latest" color="success" variant="tonal" size="x-small" class="rounded-pill">latest</v-chip>
        </div>

        <!-- Changes grouped by type -->
        <div class="d-flex flex-column" style="gap: 16px;">
          <div v-for="group in release.groups" :key="group.type">
            <div class="d-flex align-center ga-2 mb-2">
              <v-icon :icon="group.icon" :color="group.color" size="16"></v-icon>
              <span class="text-overline font-weight-black" :style="`color: rgb(var(--v-theme-${group.color}));`">{{ group.label }}</span>
            </div>
            <ul class="pl-4" style="list-style: none; padding-left: 0;">
              <li
                v-for="(item, i) in group.items"
                :key="i"
                class="text-body-2 text-grey-lighten-1 d-flex align-start ga-2 mb-1"
              >
                <span style="opacity: 0.4; flex-shrink: 0;">—</span>
                <span>{{ item }}</span>
              </li>
            </ul>
          </div>
        </div>

        <v-divider class="mt-6" style="opacity: 0.06;"></v-divider>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
const releases = [
  {
    version: '1.3.0',
    date: '19 March 2026',
    latest: true,
    groups: [
      {
        type: 'new',
        label: 'New',
        icon: 'mdi-plus-circle',
        color: 'success',
        items: [
          'Indonesian Game Dev Studios directory — browse studios, see their games, and check open job vacancies',
          'Studio profiles now show their banner and profile picture',
          'Studios with a games list display it under a Games tab',
          'Studios with job openings show them under a Jobs tab',
          'When visiting a studio\'s profile page, a Jobs tab appears if they have open vacancies',
          'Sponsored banners in the sidebar that refresh on every page visit',
          'Discover shortcut button for smaller screens',
          'This changelog page',
        ]
      },
      {
        type: 'improved',
        label: 'Improved',
        icon: 'mdi-arrow-up-circle',
        color: 'primary',
        items: [
          'My Discoveries is now collapsed by default for a cleaner sidebar',
          'Links, mentions, and hashtags in studio descriptions are now clickable',
          '"See Profile" button in the studio popup with a subtle highlight animation',
          'Studio popup no longer gets squished on smaller screens',
          'Discover panel now has a clearer icon and a close button',
        ]
      },
    ]
  },
  {
    version: '1.2.0',
    date: '15–19 March 2026',
    latest: false,
    groups: [
      {
        type: 'new',
        label: 'New',
        icon: 'mdi-plus-circle',
        color: 'success',
        items: [
          'Studios directory with search and detailed studio popup',
          'Admins can add, edit, and remove studios from the directory',
          'Admins can pin announcements to the home feed',
          'Admins can manage sidebar banner ads',
          'Studio handles and website links are clickable',
          'Clicking a studio handle opens their profile inside the app',
          'Event calendar — view and parse gamedev events from posts',
          'GIF support in posts',
          'Instagram post embeds',
          'Compose post with media only (no text required)',
          'Auto-select hashtag when posting from a tag feed',
        ]
      },
      {
        type: 'improved',
        label: 'Improved',
        icon: 'mdi-arrow-up-circle',
        color: 'primary',
        items: [
          'Notifications are more accurate and grouped better',
          'Links without explicit tagging now open correctly',
          'Posting and replying with images is more reliable',
        ]
      },
    ]
  },
  {
    version: '1.1.0',
    date: '8–13 March 2026',
    latest: false,
    groups: [
      {
        type: 'new',
        label: 'New',
        icon: 'mdi-plus-circle',
        color: 'success',
        items: [
          'Custom saved searches in the Discover sidebar',
          'Stories — write and share long-form content with your audience',
          'Portfolio editor: showcase your games, roles, and experience',
          'Export your portfolio as a standalone page',
          'Direct messages via chat',
          'Notifications panel',
          'Quote posts',
          'Image replies',
          'Mention suggestions when typing @',
          'Search for posts and people',
          'Blocklist management',
          'Focus mode for distraction-free reading',
        ]
      },
    ]
  },
  {
    version: '1.0.0',
    date: '8 March 2026',
    latest: false,
    groups: [
      {
        type: 'new',
        label: 'New',
        icon: 'mdi-plus-circle',
        color: 'success',
        items: [
          'Gamedev.id is live! 🎉',
          'Curated feed for Indonesian game developers',
          'Log in with your Bluesky account',
          'Post, reply, like, and repost',
          'Bookmarks with custom groups',
          'Lists support',
          'Profile page with About Me, Posts, Bookmarks, Stories, and Contact tabs',
        ]
      },
    ]
  },
];
</script>
